import React, { useState, useContext, useEffect } from 'react';
import { useMutation, gql } from '@apollo/client';
import '@nile/components/src/Components/AI/styles/ratingComponentStyles.scss';
import { DisplayText, TextField, Button } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import {
  StarRating,
  Loader,
  getCurrentUserHook,
  getCookieValue
} from '@nile/components';
import { useAIStore } from '@nile/components/src/Components/AI/store/aiStore.tsx';
import { AIOnboardingHook } from '@nile/components/src/Components/AI/hooks/AIOnboardingHook';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import {
  AccountClient,
  SAVE_PERSONALIZATION,
  getPersonalization
} from '@nile/graphql';

const CREATE_AI_FEATURE_FEEDBACK = gql`
  mutation createAIFeatureFeedback($input: CreateAIFeatureFeedbackInput!) {
    createAIFeatureFeedback(input: $input) {
      feedbackId
      ratingType
      rating
      # comment
    }
  }
`;

interface AIOnboardingRatingComponentProps {
  onCompleted?: () => void;
  featureName?: string;
  variant?: 'default' | 'transparent';
  nocomments?: boolean;
}

const AIOnboardingRatingComponent = ({
  onCompleted,
  featureName,
  variant = 'default',
  nocomments = false
}: AIOnboardingRatingComponentProps) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [hasRated, setHasRated] = useState(false);
  const [checkingRating, setCheckingRating] = useState(true);
  const [
    addReview,
    { loading, error }
  ] = useMutation(CREATE_AI_FEATURE_FEEDBACK, { client: AccountClient });

  const [savePersonalization] = useMutation(SAVE_PERSONALIZATION, {
    client: AccountClient,
    onError(err) {
      console.error('Error saving personalization:', err);
    }
  });

  const aiOnboardingHook = AIOnboardingHook();
  const aiFeatureHook = AIFeaturesHook();
  const currentUser = getCurrentUserHook();
  const closeRatingComponent = useAIStore((s) => s.closeRatingComponent);
  const marketplaceToken = getCookieValue('so-marketplace-session') || '';

  const featureId = (() => {
    const currentFeature = featureName;
    if (currentFeature) {
      const featureData = aiFeatureHook?.getAIFeatureData(currentFeature);
      return featureData?.featureId;
    }
    return null;
  })();

  useEffect(() => {
    if (featureId && currentUser?.userId) {
      setCheckingRating(true);
      getPersonalization({
        userId: currentUser.userId,
        category: 'aiOnboarding',
        name: `rating_${featureId}`
      })
        .then((response) => {
          const savedData = response?.data?.getPersonalizationByCategoryAndName;
          if (savedData && savedData.value) {
            setHasRated(true);
          }
        })
        .catch((err) => {
          console.error('Error fetching rating personalization:', err);
        })
        .finally(() => {
          setCheckingRating(false);
        });
    } else {
      setCheckingRating(false);
    }
  }, [featureId, currentUser?.userId]);

  const handleRatingApi = async (currentRating?: number) => {
    const ratingToUse = currentRating ?? rating;
    if (!ratingToUse || !featureId) {
      console.warn('Rating submission failed: missing rating or featureId', {
        rating: ratingToUse,
        featureId
      });
      return;
    }

    try {
      await addReview({
        variables: {
          input: {
            feature: {
              featureId: featureId
            },
            ratingType: 'STAR',
            rating: ratingToUse,
            comment: comment || null
          }
        }
      });

      if (currentUser?.userId) {
        const personalisedData = {
          user: {
            userId: currentUser?.userId
          },
          category: 'aiOnboarding',
          name: `rating_${featureId}`,
          value: JSON.stringify({ rating: ratingToUse, comment })
        };
        await savePersonalization({
          input: personalisedData,
          variables: personalisedData
        });
        setHasRated(true);
      }

      // Reset form after successful submission
      setRating(0);
      setComment('');

      closeRatingComponent();
      if (onCompleted) {
        onCompleted();
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      if (onCompleted) {
        onCompleted();
      }
      closeRatingComponent();
    }
  };

  if (!marketplaceToken || checkingRating || hasRated) {
    return null;
  }

  const setRatingWrapper = (num: number) => {
    setRating(num);
    if (nocomments) {
      handleRatingApi(num);
    }
  };

  return (
    <div className="ai-onboarding-rating-component-wrapper position-absolute top">
      <div
        className={`rating-holder ratingBubbleRadius ${
          variant === 'transparent'
            ? 'bg-transparent'
            : 'bg-aiButton px-10 py-2'
        }`}
      >
        <div className={`flex center g10 ${rating ? 'jcenter' : ''}`}>
          {!rating && (
            <DisplayText
              variant={variant === 'transparent' ? 'subdued' : 'bold'}
              size="medium"
              weight="Bold"
            >
              Rate your experience
            </DisplayText>
          )}
          <StarRating
            rating={rating}
            onRatingChange={(num) => {
              setRatingWrapper(num);
            }}
            size="lg"
            color={variant === 'transparent' ? 'black' : 'white'}
          />
        </div>
        {rating > 0 && !nocomments && (
          <div className="flex center g5 color-white rating-comment-box">
            <TextField
              bgTransparent
              noborder
              multiline
              type="text"
              placeholder="Add a comment"
              size="small"
              value={comment}
              onChange={(val) => setComment(val)}
            />
            <span className="pointer" onClick={() => handleRatingApi()}>
              {!loading && <SVGIcon name="SendWhite" size="md" color="white" />}
            </span>
            {loading && <Loader />}
            {/* <Button
              icon={<SVGIcon name="SendWhite" size="md" color="white" />}
              size="md"
              onClick={handleRatingApi}
              loading={loading}
              backgroundColor="transparent"
              plainSecondary
              // color="white"
              iconColor="white"
            /> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIOnboardingRatingComponent;
